import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import api from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { Search, Filter, ChevronRight, AlertTriangle } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function relative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

function NotStartedRow({ r }) {
  const allowed = 1 + (r.parent_paid_slots || 0);
  return (
    <div data-testid={`admin-family-row-${r.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 items-center border-b border-white/5 last:border-0 bg-amber-300/[0.03]">
      <div className="col-span-12 md:col-span-3">
        <span data-testid={`admin-family-not-started-${r.parent_id}`} className="inline-flex px-2 py-1 rounded-full text-[11px] font-semibold bg-amber-300/15 text-amber-300">Not started</span>
        <div className="text-xs text-slate-400 mt-1">No child enrolled yet</div>
      </div>
      <div className="col-span-12 md:col-span-3">
        <div className="text-sm text-slate-200">{r.parent?.name || "—"}</div>
        <div className="text-xs text-slate-400 truncate">{r.parent?.email}</div>
      </div>
      <div className="col-span-6 md:col-span-2 text-xs text-slate-500">—</div>
      <div className="col-span-3 md:col-span-1 text-center text-xs text-slate-500">—</div>
      <div className="col-span-6 md:col-span-1 text-center">
        <div data-testid={`admin-family-slots-${r.id}`} className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-400/15 text-emerald-300" title={`${allowed} child slot${allowed === 1 ? "" : "s"} open`}>0/{allowed}</div>
      </div>
      <div className="col-span-6 md:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Signed up</div>
        <div className="text-xs text-slate-300">{fmtDate(r.last_activity_at)}</div>
      </div>
    </div>
  );
}

export default function AdminFamilies() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tribe, setTribe] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ include_not_started: "true" });
    if (q.trim()) params.set("q", q.trim());
    if (tribe) params.set("tribe", tribe);
    const t = setTimeout(() => {
      api.get(`/admin/families?${params}`)
        .then(({ data }) => { setRows(data); setLoading(false); })
        .catch((e) => { setErr(e?.response?.data?.detail || "Failed to load"); setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, tribe]);

  const tribes = useMemo(() => Object.values(TRIBES), []);

  const [scrubOpen, setScrubOpen] = useState(false);
  const [scrubText, setScrubText] = useState("");
  const [scrubLoading, setScrubLoading] = useState(false);
  const [scrubResult, setScrubResult] = useState(null);
  const [scrubErr, setScrubErr] = useState("");

  const runScrub = async () => {
    setScrubErr("");
    setScrubLoading(true);
    try {
      const { data } = await api.post("/admin/danger/scrub-all-parent-data", { confirm: "PURGE ALL" });
      setScrubResult(data?.deleted || {});
      setScrubText("");
      // Reload the families list so it shows 0 rows.
      setRows([]);
    } catch (e) {
      setScrubErr(e?.response?.data?.detail || "Scrub failed");
    } finally {
      setScrubLoading(false);
    }
  };

  return (
    <AdminShell subtitle="Refund audit tool" title="All enrolled families">
      <div className="rounded-2xl bg-[#151737] border border-white/10 p-4 md:p-5 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-families-search"
              placeholder="Search by child name or parent email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0F1020] border border-white/10 focus:border-amber-300 focus:outline-none text-sm text-slate-100 placeholder-slate-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            <select
              data-testid="admin-families-tribe-filter"
              value={tribe}
              onChange={(e) => setTribe(e.target.value)}
              className="py-2.5 pl-3 pr-8 rounded-xl bg-[#0F1020] border border-white/10 focus:border-amber-300 focus:outline-none text-sm text-slate-100"
            >
              <option value="">All tribes</option>
              {tribes.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          </div>
          <div className="text-xs text-slate-400">{loading ? "Loading…" : `${rows.length} result${rows.length === 1 ? "" : "s"}`}</div>
          <button
            data-testid="admin-families-scrub-open"
            onClick={() => { setScrubOpen(true); setScrubResult(null); setScrubErr(""); setScrubText(""); }}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/10 text-rose-300 border border-rose-500/30 hover:bg-rose-500/20 transition-colors"
            title="Danger zone: delete every parent + all their data"
          >
            <AlertTriangle size={12} /> Scrub all parent data
          </button>
        </div>
      </div>

      {scrubOpen && (
        <div data-testid="admin-scrub-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-[#151737] border border-rose-500/40 p-6 space-y-4">
            <div className="flex items-center gap-2 text-rose-300">
              <AlertTriangle size={20} />
              <div className="font-display text-xl font-bold">Danger zone</div>
            </div>
            <div className="text-sm text-slate-300 space-y-2">
              <p>This will permanently delete <strong>every non-admin user</strong> and everything scoped to them on production:</p>
              <ul className="list-disc pl-5 text-slate-400 space-y-0.5 text-xs">
                <li>All parent accounts</li>
                <li>All children, checkins, certificates, roadblocks, providers</li>
                <li>All exercise / learn / session-view logs, check-in reminder acks</li>
                <li>All pending_purchase_credits and processed_webhooks (Kajabi idempotency)</li>
                <li>All admin_activity audit rows (kept: this one scrub row)</li>
              </ul>
              <p>Preserved: admin accounts, CMS content overrides, certificate templates, files.</p>
              <p className="text-rose-200"><strong>This cannot be undone.</strong> Type <code className="bg-rose-500/20 px-1.5 py-0.5 rounded">PURGE ALL</code> below to confirm.</p>
            </div>
            <input
              data-testid="admin-scrub-confirm-input"
              type="text"
              value={scrubText}
              onChange={(e) => setScrubText(e.target.value)}
              placeholder='Type "PURGE ALL"'
              className="w-full px-3 py-2 rounded-lg bg-[#0F1020] border border-white/10 focus:border-rose-400 focus:outline-none text-sm text-slate-100 placeholder-slate-500"
            />
            {scrubErr && <div className="text-xs text-rose-300" data-testid="admin-scrub-error">{scrubErr}</div>}
            {scrubResult && (
              <div data-testid="admin-scrub-result" className="text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-emerald-200">
                <div className="font-semibold mb-1">✓ Scrub complete. Rows deleted:</div>
                <pre className="whitespace-pre-wrap font-mono text-[10px]">{JSON.stringify(scrubResult, null, 2)}</pre>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                data-testid="admin-scrub-cancel"
                onClick={() => setScrubOpen(false)}
                disabled={scrubLoading}
                className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"
              >Close</button>
              <button
                data-testid="admin-scrub-confirm"
                onClick={runScrub}
                disabled={scrubText !== "PURGE ALL" || scrubLoading}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-40 disabled:cursor-not-allowed"
              >{scrubLoading ? "Scrubbing…" : "Scrub everything"}</button>
            </div>
          </div>
        </div>
      )}

      {err && <div className="mb-4 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="admin-families-error">{err}</div>}

      <div className="rounded-2xl bg-[#151737] border border-white/10 overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5 font-semibold">
          <div className="col-span-3">Child</div>
          <div className="col-span-3">Parent</div>
          <div className="col-span-2">Tribe</div>
          <div className="col-span-1 text-center">Session</div>
          <div className="col-span-1 text-center">Slots</div>
          <div className="col-span-2">Last activity</div>
        </div>
        {rows.length === 0 && !loading && (
          <div className="px-5 py-10 text-center text-slate-400 text-sm" data-testid="admin-families-empty">No families match those filters.</div>
        )}
        {rows.map((r) => {
          if (r.not_started) return <NotStartedRow key={r.id} r={r} />;
          const t = TRIBES[r.tribe];
          return (
            <Link
              key={r.id}
              to={`/admin/families/${r.id}`}
              data-testid={`admin-family-row-${r.id}`}
              className="grid grid-cols-12 gap-4 px-5 py-4 items-center border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
            >
              <div className="col-span-12 md:col-span-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 md:hidden">Child</div>
                <div className="font-semibold text-white">{r.name}</div>
                <div className="text-xs text-slate-400">Age {r.age}</div>
              </div>
              <div className="col-span-12 md:col-span-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 md:hidden">Parent</div>
                <div className="text-sm text-slate-200">{r.parent?.name || "—"}</div>
                <div className="text-xs text-slate-400 truncate">{r.parent?.email}</div>
              </div>
              <div className="col-span-6 md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 md:hidden">Tribe</div>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: (t?.accent || "#94a3b8") + "22", color: t?.accent || "#94a3b8" }}>
                  {t?.name || r.tribe}
                </span>
              </div>
              <div className="col-span-3 md:col-span-1 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 md:hidden">Session</div>
                <div className="font-display text-lg font-bold text-white">{r.current_session}<span className="text-xs text-slate-500">/12</span></div>
              </div>
              <div className="col-span-6 md:col-span-1 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 md:hidden">Paid slots</div>
                {(() => {
                  const slots = r.parent_paid_slots || 0;
                  const used = r.parent_enrolled_count || 0;
                  const allowed = 1 + slots; // 1 free + add-ons
                  const mismatch = used > allowed;
                  return (
                    <div data-testid={`admin-family-slots-${r.id}`} className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${mismatch ? "bg-rose-400/15 text-rose-300" : "bg-emerald-400/15 text-emerald-300"}`} title={`${used} enrolled · ${slots} add-on slot${slots === 1 ? "" : "s"} purchased · ${allowed} total allowed`}>
                      {used}/{allowed}
                    </div>
                  );
                })()}
              </div>
              <div className="col-span-6 md:col-span-2 flex items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 md:hidden">Last activity</div>
                  <div className="text-xs text-slate-300">{relative(r.last_activity_at)}</div>
                  <div className="text-[10px] text-slate-500">{fmtDate(r.last_activity_at)}</div>
                </div>
                <ChevronRight size={16} className="text-slate-500 shrink-0" />
              </div>
            </Link>
          );
        })}
      </div>
    </AdminShell>
  );
}
